import {forgetUploadEta, resetUploadEta} from './eta/upload-eta-registry';
import {FileEntry} from '@app/gen/schemas/file-entry';
import {UploadType} from '@app/site-config';
import {MessageDescriptor} from '@ui/i18n/message-descriptor';
import {Settings} from '@ui/settings/settings';
import {
  UploadedFile,
  UploadedFileFromEntry,
} from '@ui/utils/files/uploaded-file';
import {Draft, enableMapSet} from 'immer';
import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {createUpload, restrictionsFromConfig} from './create-file-upload';
import {ProgressTimeout} from './progress-timeout';
import {startUploading} from './start-uploading';
import {S3MultipartUpload} from './strategy/s3-multipart-upload';
import {discardServerUpload, matchesInterrupted} from './persist-uploads';
import {UploadStrategy, UploadStrategyConfig} from './strategy/upload-strategy';

enableMapSet();

export interface FileUpload {
  file: UploadedFile | UploadedFileFromEntry;
  percentage: number;
  bytesUploaded: number;
  // 'aborted' = paused (bytes on the server are kept, Resume continues).
  // 'interrupted' = left over from a previous visit; needs the file re-selected.
  status:
    | 'pending'
    | 'inProgress'
    | 'aborted'
    | 'failed'
    | 'completed'
    | 'interrupted';
  errorMessage?: string | MessageDescriptor | null;
  entry?: FileEntry;
  request?: UploadStrategy;
  timer?: ProgressTimeout;
  options: UploadStrategyConfig;
  meta?: unknown;
  // Paused with the Pause button (not auto-resumed when the network returns).
  pausedByUser?: boolean;
  // Automatic retries after network errors (backoff 2s, 5s, 10s).
  autoRetries?: number;
  // How a restored upload continues: server offset/parts or from the start.
  resumable?: 'tus' | 's3-multipart' | 'restart';
  savedAt?: number;
  // Time actually spent uploading in this tab (pauses excluded), for "Done · 2 min".
  activeMs?: number;
  activeSince?: number | null;
}

interface FileUploadStoreState {
  // If false, then we are showing file selection list UI where user can manage selected files.
  uploadStarted: boolean;
  concurrency: number;
  fileUploads: Map<string, FileUpload>;
  // uploads with pending and inProgress status
  activeUploadsCount: number;
  // uploads with inProgress status
  inProgressUploadsCount: number;
  pendingUploadsCount: number;
  completedUploadsCount: number;
  pausedUploadsCount: number;
  failedUploadsCount: number;
  interruptedUploadsCount: number;
}

const initialState: FileUploadStoreState = {
  uploadStarted: false,
  concurrency: 3,
  fileUploads: new Map(),
  activeUploadsCount: 0,
  inProgressUploadsCount: 0,
  pendingUploadsCount: 0,
  completedUploadsCount: 0,
  pausedUploadsCount: 0,
  failedUploadsCount: 0,
  interruptedUploadsCount: 0,
};

export const DEFAULT_PARALLEL_FILES = 3;
const MAX_PARALLEL_MULTIPART = 2;

function parallelFilesSetting(settings: Settings): number {
  const raw = Number(settings.uploading?.parallel_files);
  if (!raw || raw < 1) return DEFAULT_PARALLEL_FILES;
  return Math.min(Math.floor(raw), 6);
}

export interface FileUploadStoreActions {
  uploadMultiple: (
    files: (File | UploadedFile)[] | FileList,
    strategyConfig: Omit<
      UploadStrategyConfig,
      // progress would be called for each upload simultaneously
      'onProgress'
    >,
    options?: {
      startUpload?: boolean;
    },
  ) => string[];
  uploadSingle: (
    file: File | UploadedFile,
    options: UploadStrategyConfig,
  ) => string;
  startUpload: () => void;
  removeUpload: (id: string) => void;
  clearInactive: () => void;
  abortUpload: (id: string) => void;
  retryUpload: (id: string) => void;
  pauseUpload: (id: string, byUser?: boolean) => void;
  resumeUpload: (id: string) => void;
  cancelUpload: (id: string) => Promise<void>;
  pauseAll: () => void;
  resumeAll: () => void;
  cancelAll: () => Promise<void>;
  reattachFiles: (files: File[] | FileList, onlyId?: string) => {
    matched: number;
    unmatched: string[];
  };
  restoreUploads: (uploads: FileUpload[]) => void;
  updateFileUpload: (id: string, state: Partial<FileUpload>) => void;
  addCompletedFileUpload: (
    entry: FileEntry,
    uploadType: keyof typeof UploadType,
  ) => void;
  getUpload: (id: string) => FileUpload | undefined;
  getCompletedFileEntries: () => FileEntry[];
  runQueue: () => Promise<void>;
  reset: () => void;
  // Optional hook called after interrupted uploads were restored.
  onRestored?: (count: number) => void;
}

export type FileUploadState = FileUploadStoreState & FileUploadStoreActions;

export interface FileUploadStoreOptions {
  modifyUploadedFile?: (file: UploadedFile) => UploadedFile;
  persistUploads?: boolean;
  // localStorage bucket for resume metadata (one per page type).
  persistKey?: string;
}

interface StoreProps {
  settings: Settings;
  options?: FileUploadStoreOptions;
}
export const createFileUploadStore = ({settings, options}: StoreProps) =>
  create<FileUploadState>()(
    immer((set, get) => {
      return {
        ...initialState,
        reset: () => {
          set(initialState);
        },

        getUpload: uploadId => {
          return get().fileUploads.get(uploadId);
        },

        getCompletedFileEntries: () => {
          return [...get().fileUploads.values()]
            .filter(u => !!u.entry)
            .map(u => u.entry) as FileEntry[];
        },

        clearInactive: () => {
          set(state => {
            state.fileUploads.forEach((upload, key) => {
              if (
                upload.status === 'completed' ||
                upload.status === 'failed'
              ) {
                forgetUploadEta(key);
                state.fileUploads.delete(key);
              }
            });
            updateTotals(state);
          });
          get().runQueue();
        },

        // Legacy "abort": cancel the upload and delete partial server data.
        abortUpload: id => {
          const upload = get().fileUploads.get(id);
          if (!upload) return;
          const request = upload.request;
          upload.timer?.done();
          if (request?.cancel) {
            void request.cancel();
          } else {
            void request?.abort();
          }
          if (!get().uploadStarted) {
            get().removeUpload(id);
          } else {
            get().updateFileUpload(id, {status: 'aborted', request: undefined});
            get().runQueue();
          }
        },

        pauseUpload: (id, byUser = true) => {
          const upload = get().fileUploads.get(id);
          if (
            !upload ||
            (upload.status !== 'inProgress' && upload.status !== 'pending')
          ) {
            return;
          }
          // Mark first so strategy error callbacks see "paused", not "failed".
          get().updateFileUpload(id, {
            status: 'aborted',
            errorMessage: null,
            pausedByUser: byUser,
            request: undefined,
          });
          upload.timer?.done();
          void upload.request?.abort();
          get().runQueue();
        },

        resumeUpload: id => get().retryUpload(id),

        cancelUpload: async id => {
          const upload = get().fileUploads.get(id);
          if (!upload) return;
          get().removeUpload(id);
          upload.timer?.done();
          try {
            if (upload.request?.cancel) {
              await upload.request.cancel();
            } else {
              await upload.request?.abort();
            }
          } catch {
            //
          }
          if (upload.status !== 'completed') {
            await discardServerUpload(upload);
          }
          get().runQueue();
        },

        pauseAll: () => {
          get().fileUploads.forEach((upload, id) => {
            if (upload.status === 'inProgress' || upload.status === 'pending') {
              get().pauseUpload(id, true);
            }
          });
        },

        resumeAll: () => {
          get().fileUploads.forEach((upload, id) => {
            if (upload.status === 'aborted' || upload.status === 'failed') {
              get().retryUpload(id);
            }
          });
        },

        cancelAll: async () => {
          const ids = [...get().fileUploads.entries()]
            .filter(([, u]) => u.status !== 'completed')
            .map(([id]) => id);
          await Promise.all(ids.map(id => get().cancelUpload(id)));
        },

        reattachFiles: (files, onlyId) => {
          const candidates = [...get().fileUploads.values()].filter(
            u =>
              u.status === 'interrupted' && (!onlyId || u.file.id === onlyId),
          );
          let matched = 0;
          const unmatched: string[] = [];
          const used = new Set<string>();
          [...files].forEach(native => {
            const target = candidates.find(
              u => !used.has(u.file.id) && matchesInterrupted(native, u),
            );
            if (!target) {
              unmatched.push(native.name);
              return;
            }
            const uploaded = new UploadedFile(
              native,
              target.file.relativePath || undefined,
            );
            uploaded.id = target.file.id;
            // Same fingerprint = same tus URL / S3 multipart id = resume at offset.
            uploaded.fingerprint = target.file.fingerprint;
            used.add(target.file.id);
            matched += 1;
            get().updateFileUpload(target.file.id, {
              file: uploaded,
              // Callbacks/restrictions are not persisted; rebuild restrictions.
              options: {
                ...target.options,
                restrictions: restrictionsFromConfig(target.options),
              },
              status: 'pending',
              errorMessage: null,
              pausedByUser: false,
              autoRetries: 0,
            });
          });
          if (matched) {
            set(state => {
              state.uploadStarted = true;
            });
            get().runQueue();
          }
          return {matched, unmatched};
        },

        retryUpload: id => {
          const upload = get().fileUploads.get(id);
          if (
            !upload ||
            (upload.status !== 'failed' && upload.status !== 'aborted')
          ) {
            return;
          }
          get().updateFileUpload(id, {
            status: 'pending',
            errorMessage: null,
            pausedByUser: false,
          });
          set(state => {
            state.uploadStarted = true;
          });
          get().runQueue();
        },

        restoreUploads: uploads => {
          if (!uploads.length) {
            return;
          }
          set(state => {
            uploads.forEach(upload => {
              if (!state.fileUploads.has(upload.file.id)) {
                state.fileUploads.set(upload.file.id, upload);
              }
            });
            updateTotals(state);
          });
        },

        updateFileUpload: (id, newUploadState) => {
          set(state => {
            const fileUpload = state.fileUploads.get(id);
            if (fileUpload) {
              const extra: Partial<FileUpload> = {};
              const nextStatus = newUploadState.status;
              if (nextStatus && nextStatus !== fileUpload.status) {
                const now = Date.now();
                // Leaving or entering "uploading": start a fresh speed estimate.
                if (
                  nextStatus === 'inProgress' ||
                  fileUpload.status === 'inProgress'
                ) {
                  resetUploadEta(id);
                }
                if (fileUpload.status === 'inProgress' && fileUpload.activeSince) {
                  extra.activeMs =
                    (fileUpload.activeMs ?? 0) + (now - fileUpload.activeSince);
                  extra.activeSince = null;
                }
                if (nextStatus === 'inProgress') {
                  extra.activeSince = now;
                }
              }
              state.fileUploads.set(id, {
                ...fileUpload,
                ...newUploadState,
                ...extra,
              });

              // only need to update inProgress count if status of the uploads in queue change
              if ('status' in newUploadState) {
                updateTotals(state);
              }
            }
          });
        },

        addCompletedFileUpload: (entry, uploadType) => {
          set(state => {
            state.fileUploads.set(`${entry.id}`, {
              file: new UploadedFileFromEntry(entry),
              status: 'completed',
              entry,
              percentage: 100,
              bytesUploaded: entry.file_size || 0,
              options: {
                uploadType,
              },
            });
            updateTotals(state);
          });
        },

        uploadSingle: (file, userOptions) => {
          const upload = createUpload(file, userOptions, options);
          const fileUploads = new Map(get().fileUploads);
          fileUploads.set(upload.file.id, upload);

          set(state => {
            updateTotals(state);
            state.fileUploads = fileUploads;
            state.uploadStarted = true;
          });

          get().runQueue();

          return upload.file.id;
        },

        removeUpload: (id: string) => {
          forgetUploadEta(id);
          set(state => {
            state.fileUploads.delete(id);
            updateTotals(state);
          });
        },

        uploadMultiple: (files, strategyConfig, {startUpload = true} = {}) => {
          // create file upload items from specified files
          const uploads = new Map<string, FileUpload>(get().fileUploads);
          [...files].forEach(file => {
            const upload = createUpload(file, strategyConfig, options);
            uploads.set(upload.file.id, upload);
          });

          // set state only once, there might be thousands of files, don't want to trigger a rerender for each one
          set(state => {
            state.uploadStarted = startUpload;
            state.fileUploads = uploads;
            updateTotals(state);
          });

          if (startUpload) {
            get().runQueue();
          }

          return [...uploads.keys()];
        },

        startUpload: () => {
          set(state => {
            state.uploadStarted = true;
          });
          get().runQueue();
        },

        runQueue: async () => {
          if (get().uploadStarted === false) return;
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return;
          }
          const limit = parallelFilesSetting(settings);
          // Start as many pending uploads as the parallel limit allows.
          while (true) {
            const uploads = [...get().fileUploads.values()];
            const active = uploads.filter(u => u.status === 'inProgress');
            const next = uploads.find(u => u.status === 'pending');
            if (!next) {
              if (!active.length) {
                set(state => {
                  state.uploadStarted = false;
                });
              }
              return;
            }
            // Files from a dropped folder go one at a time (no duplicate folders).
            const folderBusy = active.some(u => u.file.relativePath);
            if (folderBusy && next.file.relativePath) return;
            const multipartActive = active.filter(
              u => u.request instanceof S3MultipartUpload,
            ).length;
            const nextIsBig =
              next.file.size >= 100 * 1024 * 1024 &&
              multipartActive >= MAX_PARALLEL_MULTIPART;
            if (active.length >= limit || nextIsBig) return;
            // Claim it before the async start so a parallel runQueue skips it.
            get().updateFileUpload(next.file.id, {status: 'inProgress'});
            try {
              await startUploading(get().getUpload(next.file.id)!, get());
            } catch (e) {
              console.error('Upload could not start', e);
              get().updateFileUpload(next.file.id, {
                status: 'failed',
                request: undefined,
              });
            }
          }
        },
      };
    }),
  );

const updateTotals = (state: Draft<FileUploadState>) => {
  const uploads = [...state.fileUploads.values()];
  state.completedUploadsCount = uploads.filter(
    u => u.status === 'completed',
  ).length;
  state.inProgressUploadsCount = uploads.filter(
    u => u.status === 'inProgress',
  ).length;
  state.pendingUploadsCount = uploads.filter(
    u => u.status === 'pending',
  ).length;
  state.activeUploadsCount = uploads.filter(
    u => u.status === 'inProgress' || u.status === 'pending',
  ).length;
  state.pausedUploadsCount = uploads.filter(u => u.status === 'aborted').length;
  state.failedUploadsCount = uploads.filter(u => u.status === 'failed').length;
  state.interruptedUploadsCount = uploads.filter(
    u => u.status === 'interrupted',
  ).length;
};
